import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Linking } from 'react-native';
import { BOOKAPT_IOS_APP_STORE_URL } from '../config/appDistributionConfig';
import { isWidgetEmbedContext } from '../utils/widgetEmbed';

const DISMISS_KEY = 'bookapt_ios_app_banner_dismissed';

/**
 * Web-only "get the app" banner for iPhone Safari visitors browsing bookapt.io.
 * Android is intentionally excluded — there is no Play Store listing yet
 * (see appDistributionConfig.ts), and a "download" banner with nowhere real
 * to send an Android visitor would be worse than no banner at all. Add an
 * Android branch here once androidLink is real.
 *
 * Also skipped inside an embedded merchant-site widget (isWidgetEmbedContext) —
 * that's a lightweight booking overlay running on someone else's site, not a
 * place to pitch BookAPT's own app.
 */
export default function MobileAppBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || typeof window === 'undefined') {
      return;
    }
    try {
      if (isWidgetEmbedContext()) return;
      if (window.localStorage.getItem(DISMISS_KEY)) return;
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
      if (isIOS) setVisible(true);
    } catch {
      // ignore — no banner if storage/UA checks fail
    }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
  };

  const openAppStore = () => {
    Linking.openURL(BOOKAPT_IOS_APP_STORE_URL);
  };

  return (
    <View style={styles.banner}>
      <Text style={styles.text} numberOfLines={2}>
        📱 Get the BookAPT app for faster booking
      </Text>
      <TouchableOpacity style={styles.cta} onPress={openAppStore}>
        <Text style={styles.ctaText}>View in App Store</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.dismiss} onPress={dismiss} accessibilityLabel="Dismiss">
        <Text style={styles.dismissText}>✕</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1d3557',
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 10,
  },
  text: {
    flex: 1,
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  cta: {
    backgroundColor: '#e76f51',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '700',
  },
  dismiss: {
    padding: 4,
  },
  dismissText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
});
